import { Box, Stack, Typography, styled, useTheme } from "@mui/material";
import { transformMetricsForDisplay } from "Charts/Utils/DashboardChartUtils";
import { GetDashboardGraphInterval, type Graph } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from "chart.js/auto";
import type React from "react";
import { Bar } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const StyledDashboardChartWrapper = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 240px; 
  width: 100%;

  & .chartMenu {
    width: 100vw;
    height: 40px;
    background: #1A1A1A;
    color: rgba(54, 162, 235, 1);
  }

  & .chartMenu p {
    padding: 10px;
    font-size: 20px;
  }

  & .chartCard {
    width: 100vw;
    height: calc(100vh - 40px);
    background: rgba(54, 162, 235, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  & .chartBox {
    width: 700px;
    padding: 20px;
    border-radius: 20px;
    border: solid 3px rgba(54, 162, 235, 1);
    background: white;
  }
`;

const Dot = styled("div")(({ color }: { color: string }) => ({
  background: color,
  width: 8,
  height: 8,
  marginRight: 8,
  borderRadius: Number.MAX_VALUE,
}));

const metricTranslationMap: {
  [key: string]: "inProgress" | "completed" | "declined";
} = {
  Rozpracováno: "inProgress",
  Dokončeno: "completed",
  "Nemá zájem": "declined",
};

type Props = {
  readonly interval: GetDashboardGraphInterval;
  readonly data: Graph;
  readonly renderControls: () => React.ReactElement;
};

type IntervalDependentOptions = {
  readonly barPercentage: number;
  readonly stepSize: number;
};

type IntervalDependentOptionsMap = {
  [key: string]: IntervalDependentOptions;
};

const intervalDependentOptionsMap: IntervalDependentOptionsMap = {
  [GetDashboardGraphInterval.all]: {
    barPercentage: 0.35,
    stepSize: 40,
  },
  [GetDashboardGraphInterval.year]: {
    barPercentage: 0.4,
    stepSize: 40,
  },
  [GetDashboardGraphInterval.half_year]: {
    barPercentage: 0.2,
    stepSize: 40,
  },
  [GetDashboardGraphInterval.month]: {
    barPercentage: 0.8,
    stepSize: 5,
  },
  [GetDashboardGraphInterval.week]: {
    barPercentage: 0.25,
    stepSize: 5,
  },
};

export const DashboardChart: React.FC<Props> = ({ data, interval, renderControls }) => {
  const theme = useTheme();
  const { t } = useResource();

  const intervalOptions = intervalDependentOptionsMap[interval];

  const options = {
    barPercentage: intervalOptions.barPercentage,
    responsive: true,
    maintainAspectRatio: false,
    skipBorder: "middle",
    borderRadius: {
      topLeft: 6,
      topRight: 6,
      bottomLeft: 6,
      bottomRight: 6,
    },
    plugins: {
      title: {
        display: false,
      },
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            let label = context.dataset.label || "";

            if (label) {
              const datasetName = metricTranslationMap[context.dataset.label];
              let dataset: number[];

              switch (datasetName) {
                case "inProgress": {
                  dataset = data.inProgress;
                  break;
                }
                case "completed": {
                  dataset = data.completed;
                  break;
                }
                case "declined": {
                  dataset = data.declined;
                  break;
                }
              }

              const metricValue = dataset[context.dataIndex];

              label += `: ${metricValue}`;
            }

            if (label) {
            }

            return label;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
        ticks: {
          color: theme.colors.textSecondary,
        },
      },
      y: {
        beginAtZero: true,
        stacked: false,
        ticks: {
          display: false,
          stepSize: intervalOptions.stepSize,
        },
        border: {
          display: false,
        },
        grid: {
          display: true,
        },
        grace: "0.5",
      },
    },
  };

  const dataForDisplay = transformMetricsForDisplay(data);

  return (
    <Stack>
      {renderControls()}
      <Box display="flex" justifyContent="flex-end" marginBottom={Spacing.L}>
        <>
          <Box display="flex" alignItems="center">
            <Dot color={theme.colors.brandLight} />
            <Typography variant="body2">{t(Resources.AnalysesListing.Status.InProgress)}</Typography>
          </Box>
          <Box display="flex" alignItems="center" marginLeft={Spacing.M}>
            <Dot color={theme.colors.brandPrimary} />
            <Typography variant="body2">{t(Resources.AnalysesListing.Status.Completed)}</Typography>
          </Box>
          <Box display="flex" alignItems="center" marginLeft={Spacing.M}>
            <Dot color={theme.colors.brandMedium} />
            <Typography variant="body2">{t(Resources.AnalysesListing.Status.Declined)}</Typography>
          </Box>
        </>
      </Box>
      <StyledDashboardChartWrapper>
        <Bar
          options={options}
          data={{
            labels: data.labels,
            datasets: [
              {
                label: t(Resources.AnalysesListing.Status.InProgress),
                data: dataForDisplay.inProgress,
                backgroundColor: theme.colors.brandLight,
                order: 2,
              },
              {
                label: t(Resources.AnalysesListing.Status.Completed),
                data: dataForDisplay.completed,
                backgroundColor: theme.colors.brandPrimary,
                order: 1,
              },
              {
                label: t(Resources.AnalysesListing.Status.Declined),
                data: dataForDisplay?.declined,
                backgroundColor: theme.colors.brandMedium,
              },
            ],
          }}
        />
      </StyledDashboardChartWrapper>
    </Stack>
  );
};
